import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ReportService } from 'src/app/dashboard/shared/services/report.service';
import { DataPointService } from 'src/app/project/shared/services/datapoint.service';
import { SelectInput } from 'src/app/shared/dropdown/shared/select-input.class';
import { Chart } from 'chart.js/auto';
import { FuelcellCurrentsDiagramComponent, FuelCellCurrentsDataPoint} from '../fuelcell-currents-diagram/fuelcell-currents-diagram.component';
import { ReportingService } from 'src/app/reporting/shared/services/reporting.service';
import { AlertService } from 'src/app/shared/alert/shared/alert.service';
import { AlertTypes } from 'src/app/shared/alert/shared/alert-types';
import { FUEL_CELL_CODE_NONE } from 'src/app/project/shared/enums/fuel-cell-status.enum';

export interface FuelCellDataPoint {
  generatorPower: number;
  generatorCurrent: number;
  generatorVoltage: number;
  loadStatusBattery: number;
  remainingFuel: number[];
  temperature: number;
  errorStatusActive: boolean;
  errorCode: string;
  fmErrorStatusActive: boolean;
  fmErrorCode: string;
  warnStatusActive: boolean;
  warnCode: string;
  time: string;
  totalRemainingFuel: number;
}

@Component({
  selector: 'avs-brennstoffzellen-history',
  templateUrl: './brennstoffzellen-history.component.html',
  styleUrls: ['./brennstoffzellen-history.component.css']
})
export class BrennstoffzellenHistoryComponent implements OnInit, OnDestroy {
  public measurementChart: any;  // ✅ Chart für Messwerte
  public errorChart: any;        // ✅ Chart für Fehler
  public samId: number;
  public deviceName: string = "";
  public now = new Date();
  public nowAsISOString: string;
  public twoDaysAgoAsString: string;
  public chartData: FuelCellDataPoint[] = [];

  // ✅ Messdaten-Arrays
  public timeLabels: string[] = [];  // ✅ Formatierte Zeit-Labels
  public generatorPower: number[] = [];
  public generatorVoltage: number[] = [];
  public generatorCurrent: number[] = [];
  public remainingFuel: number[] = [];
  public loadStatusBattery: number[] = [];
  public temperature: number[] = [];

  // ✅ Error/Warning-Arrays
  public errorWarningLevels: number[] = [];
  public errorDetails: string[] = [];

  // Dropdown-Konfiguration
  public isChannelDropdownOpen: boolean = false;
  public channelSelect: SelectInput[] = [{ name: 'Kanal 1', id: 1 }];
  public selectedChannel: SelectInput = { name: 'Kanal 1', id: 1 };

  public isIntervallDropdownOpen: boolean = false;
  public intervallSelect: SelectInput[] = [
    { name: '5 Minuten', id: 5 },
    { name: '15 Minuten', id: 15 },
    { name: '30 Minuten', id: 30 },
    { name: '60 Minuten', id: 60 }
  ];
  public selectedIntervall: SelectInput = { name: '5 Minuten', id: 5 };

  // Zeitfilter
  public fromDate: number = 2;
  public toDate: number = 0;
  public dataAge: number = 0;

  // EFOY Service-URL
  public efoyBaseURL: string = `https://www.efoy-pro.com/service/servicetool/?product=efoy-pro-2800&errorcode=`;

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: any,
    private dialog: MatDialog,
    private dataPointService: DataPointService,
    private reportService: ReportService,
    private reportingService: ReportingService,
    private dialogRef: MatDialogRef<BrennstoffzellenHistoryComponent>,
    private alertService: AlertService
  ) {
    const today = new Date();
    const diff = today.getTime() - today.getTime();
    const diffDays = diff / (1000 * 60 * 60 * 24);
    this.dataAge = diffDays;

    if (this.dataAge !== undefined && this.dataAge > 1) {
      this.toDate = Math.round(this.dataAge);
      this.fromDate = Math.round(this.dataAge) + 2;
    }

    this.samId = this.data.data.routeStation.samId;
    this.deviceName = this.data.data.routeStation.name;
    this.nowAsISOString = this.now.toISOString();

    const d = new Date();
    d.setDate(d.getDate() - 2);
    this.twoDaysAgoAsString = d.toISOString();

    const radarCount = this.data.data.schemaInfo?.radars?.length ?? 0;
    if (radarCount > 1) {
      const length = radarCount;
      for (let i = 2; i <= length; i++) {
        this.channelSelect.push({ name: 'Kanal ' + i, id: i });
      }
    }
  }

  ngOnDestroy(): void {
    this.measurementChart?.destroy();
    this.errorChart?.destroy();
  }

  ngOnInit(): void {
    this.getData();
  }

  public exportChart(): void {
    const canvas1 = document.getElementById('MeasurementChart') as HTMLCanvasElement;
    const canvas2 = document.getElementById('ErrorChart') as HTMLCanvasElement;

    if (canvas1 && canvas2) {
      // ✅ Kombiniere beide Charts in einem Export
      const combinedCanvas = document.createElement('canvas');
      combinedCanvas.width = canvas1.width;
      combinedCanvas.height = canvas1.height + canvas2.height + 20;

      const ctx = combinedCanvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, combinedCanvas.width, combinedCanvas.height);
        ctx.drawImage(canvas1, 0, 0);
        ctx.drawImage(canvas2, 0, canvas1.height + 20);

        const dataURL = combinedCanvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.href = dataURL;
        link.download = `brennstoff-verlauf-${this.samId}-${new Date().toISOString().split('T')[0]}.png`;
        link.click();
      }
    }
  }

  private getData(): void {
    this.resetDataArrays();

    const filter = {
      utcFrom: this.twoDaysAgoAsString,
      utcTo: this.nowAsISOString,
      samId: this.samId
    };

    this.reportingService.getFuelCellGraphics(filter).subscribe((response: any) => {
      // ✅ Handle beide Response-Formate
      const data = Array.isArray(response) ? response : response?.data || [];

      if (!data || data.length === 0) {
        this.alertService.alert('Für den ausgewählten Zeitraum sind keine Daten vorhanden.', AlertTypes.info);
        return;
      }

      this.chartData = data;
      console.log('✅ Daten empfangen:', this.chartData.length, 'Punkte');

      this.processChartData();
      this.createCharts();
    });
  }

  private resetDataArrays(): void {
    this.timeLabels = [];
    this.generatorPower = [];
    this.generatorVoltage = [];
    this.generatorCurrent = [];
    this.loadStatusBattery = [];
    this.temperature = [];
    this.remainingFuel = [];
    this.errorWarningLevels = [];
    this.errorDetails = [];
  }

  private processChartData(): void {
    this.chartData.forEach((dataPoint: FuelCellDataPoint, index: number) => {
      // ✅ Zeit-Label formatieren (dd.MM HH:mm)
      const formattedTime = this.formatDateTime(dataPoint.time);
      this.timeLabels.push(dataPoint.time);

      // ✅ Messdaten
      this.generatorPower.push(dataPoint.generatorPower || 0);
      this.generatorVoltage.push(dataPoint.generatorVoltage || 0);
      this.generatorCurrent.push(dataPoint.generatorCurrent || 0);
      this.loadStatusBattery.push(dataPoint.loadStatusBattery <= 100 ? dataPoint.loadStatusBattery: -1);
      this.temperature.push(dataPoint.temperature || 0);
      this.remainingFuel.push(dataPoint.totalRemainingFuel || 0);

      // ✅ Error/Warning-Level bestimmen
      const errorLevel = this.determineErrorLevel(dataPoint);
      this.errorWarningLevels.push(errorLevel);

      // ✅ Error-Details speichern
      const errorDetail = this.buildErrorDetail(dataPoint);
      this.errorDetails.push(errorDetail);
    });

    console.log('📊 Verarbeitete Daten:');
    console.log('  Time Labels:', this.timeLabels.length, '- Erste 3:', this.timeLabels.slice(0, 3));
    console.log('  Generator Power:', this.generatorPower.length, '- Erste 3:', this.generatorPower.slice(0, 3));
    console.log('  Error Levels:', this.errorWarningLevels.length, '- Fehler gefunden:', this.errorWarningLevels.filter(v => v > 0).length);
  }

  private formatDateTime(isoString: string): string {
    // ✅ Parse ISO-String korrekt
    const date = new Date(isoString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}.${month} ${hours}:${minutes}`;
  }

  private determineErrorLevel(dataPoint: FuelCellDataPoint): number {
    if (dataPoint.errorStatusActive && dataPoint.errorCode !== FUEL_CELL_CODE_NONE) {
      return 80;
    }
    if (dataPoint.fmErrorStatusActive && dataPoint.fmErrorCode !== FUEL_CELL_CODE_NONE) {
      return 60;
    }
    if (dataPoint.warnStatusActive && dataPoint.warnCode !== FUEL_CELL_CODE_NONE) {
      return 40;
    }
    return 0;
  }

  private buildErrorDetail(dataPoint: FuelCellDataPoint): string {
    const details: string[] = [];

    if (dataPoint.errorStatusActive && dataPoint.errorCode !== FUEL_CELL_CODE_NONE) {
      details.push(`🔴 Fehlercode: ${dataPoint.errorCode}`);
    }
    if (dataPoint.fmErrorStatusActive && dataPoint.fmErrorCode !== FUEL_CELL_CODE_NONE) {
      details.push(`🟠 FM Fehlercode: ${dataPoint.fmErrorCode}`);
    }
    if (dataPoint.warnStatusActive && dataPoint.warnCode !== FUEL_CELL_CODE_NONE) {
      details.push(`🟡 Warncode: ${dataPoint.warnCode}`);
    }

    return details.join('\n');
  }

  public onChannelItemClicked(item: SelectInput): void {
    this.isChannelDropdownOpen = false;
    this.selectedChannel = item;
  }

  public onChannelDropdownClicked(): void {
    this.isChannelDropdownOpen = !this.isChannelDropdownOpen;
  }

  public onIntervallItemClicked(item: SelectInput): void {
    this.isIntervallDropdownOpen = false;
    this.selectedIntervall = item;
  }

  public onIntervallDropdownClicked(): void {
    this.isIntervallDropdownOpen = !this.isIntervallDropdownOpen;
  }

  public removeData(chart: any): void {
    if (this.measurementChart) this.measurementChart.destroy();
    if (this.errorChart) this.errorChart.destroy();
    this.getData();
  }

  public setFromDate(date: number): void {
    this.fromDate = date;
    const d = new Date();
    d.setDate(d.getDate() - this.fromDate);
    this.twoDaysAgoAsString = d.toISOString();
  }

  public setToDate(date: number): void {
    this.toDate = date;
    const d = new Date();
    d.setDate(d.getDate() - this.toDate);
    this.nowAsISOString = d.toISOString();
  }

  public onCloseDialogClicked(): void {
    this.dialogRef.close();
  }

  private createCharts(): void {
    // ✅ Zerstöre alte Charts
    if (this.measurementChart) this.measurementChart.destroy();
    if (this.errorChart) this.errorChart.destroy();

    // ✅ Warte bis DOM bereit ist
    setTimeout(() => {
      this.createMeasurementChart();
      this.createErrorChart();
    }, 100);
  }

  private createMeasurementChart(): void {
    const canvas = document.getElementById('MeasurementChart') as HTMLCanvasElement;
    if (!canvas) {
      console.error('❌ MeasurementChart Canvas nicht gefunden!');
      return;
    }
    console.log('measurementchart   Time Labels:', this.timeLabels.length, '- Erste 3:', this.timeLabels.slice(0, 3));

    this.measurementChart = new Chart(canvas, {
      type: 'line',
      data: {
        labels: this.timeLabels,
        datasets: [
          {
            label: "Generatorleistung in Watt",
            data: this.generatorPower,
            borderColor: 'rgba(0, 0, 255, 0.8)',
            backgroundColor: 'rgba(0, 0, 255, 0.1)',
            tension: 0.1,
            pointRadius: 1,
            borderWidth: 2,
            fill: false
          },
          {
            label: "Generatorspannung in V",
            data: this.generatorVoltage,
            borderColor: 'rgba(255, 0, 0, 0.8)',
            tension: 0.1,
            pointRadius: 1,
            borderWidth: 2,
            fill: false
          },
          {
            label: "Generatorstrom in A",
            data: this.generatorCurrent,
            borderColor: 'rgba(0, 255, 255, 0.8)',
            tension: 0.1,
            pointRadius: 1,
            borderWidth: 2,
            fill: false
          },
          {
            label: "Ladezustand Batterie in %",
            data: this.loadStatusBattery,
            borderColor: 'rgba(255, 180, 0, 0.8)',
            tension: 0.1,
            pointRadius: 1,
            borderWidth: 2,
            fill: false
          },
          {
            label: "Temperatur in °C",
            data: this.temperature,
            borderColor: 'rgba(0, 0, 0, 0.8)',
            tension: 0.1,
            pointRadius: 1,
            borderWidth: 2,
            fill: false
          },
          {
            label: "Verbleibender Gesamtkraftstoff in %",
            data: this.remainingFuel,
            borderColor: 'rgba(165, 42, 42, 0.8)',
            tension: 0.1,
            pointRadius: 1,
            borderWidth: 2,
            fill: false
          }
        ]
      },
      options: {
        aspectRatio:5.0,
        scales: {
          x: {
            type: 'time',
            time: {
              // Parser entfernt – ISO wird direkt erkannt
              unit: 'hour',
              displayFormats: {
                hour: 'dd.MM HH:mm',
                minute: 'dd.MM HH:mm',
              },
            },
            title: {
              display: true,
              text: 'Zeit'
            },
            ticks: {
              maxRotation: 45,
              minRotation: 45,
              autoSkip: true,
              maxTicksLimit: 20
            }
          },
          y: {
            title: {
              display: true,
              text: 'Messwerte'
            },
            min: -2,
            max: 150,
            position: 'left',
          }
        },
        interaction: {
          mode: 'index',
          intersect: false
        },
        onClick: (evt: any) => {
          this.handleMeasurementChartClick(evt);
        },
        plugins: {
          tooltip: {
            callbacks: {
              title: (tooltipItems: any) => {
                const index = tooltipItems[0].dataIndex;
                return this.formatDateTime(this.timeLabels[index]);
              }
            }
          },
          legend: {
            position: 'top',
            labels: {
              boxWidth: 20,
              font: {
                size: 11
              }
            }
          }
        }
      },
      plugins: [
        {
          id: 'customCanvasBackgroundColor',
          beforeDraw: (chart: any) => {
            const ctx = chart.ctx;
            ctx.save();
            ctx.globalCompositeOperation = 'destination-over';
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, chart.width, chart.height);
            ctx.restore();
          }
        }
      ]
    });
  }

  private createErrorChart(): void {
    const canvas = document.getElementById('ErrorChart') as HTMLCanvasElement;
    if (!canvas) {
      console.error('❌ ErrorChart Canvas nicht gefunden!');
      return;
    }

    this.errorChart = new Chart(canvas, {
      type: 'bar',
      data: {
        labels: this.timeLabels,
        datasets: [
          {
            label: "Fehler/Warnung",
            data: this.errorWarningLevels,
            backgroundColor: (context: any) => {
              const value = context.raw as number;
              if (value >= 80) return 'rgba(221, 26, 0, 0.8)';
              if (value >= 60) return 'rgba(255, 153, 0, 0.8)';
              if (value >= 40) return 'rgba(255, 255, 0, 0.8)';
              return 'rgba(0, 0, 0, 0)';
            },
            borderColor: (context: any) => {
              const value = context.raw as number;
              if (value >= 80) return 'rgba(140, 15, 0, 1)';
              if (value >= 60) return 'rgba(200, 120, 0, 1)';
              if (value >= 40) return 'rgba(200, 200, 0, 1)';
              return 'rgba(0, 0, 0, 0)';
            },
            borderWidth: 1,
            barPercentage: 1.0,
            categoryPercentage: 1.0
          }
        ]
      },
      options: {
        aspectRatio:5.0,
        layout: {
          padding: {
          left: 13,
          right: 0,
          top: 0,
          bottom: 0
          }
        },
        scales: {
          x: {
            type: 'time',
            time: {
              // Parser entfernt – ISO wird direkt erkannt
              unit: 'hour',
              displayFormats: {
                hour: 'dd.MM HH:mm',
                minute: 'dd.MM HH:mm',
              },
            },
            title: {
              display: true,
              text: 'Zeit'
            },
            ticks: {
              maxRotation: 45,
              minRotation: 45,
              autoSkip: true,
              maxTicksLimit: 20
            }
          },
          y: {
            title: {
              display: true,
              text: 'Fehler-Level'
            },
            min: 0,
            max: 100,
            ticks: {
              callback: (value: any) => {
                if (value === 0) return '';
                /*                 if (value === 80) return 'Fehler';
                                if (value === 60) return 'FM-Fehler';
                                if (value === 40) return 'Warnung';
                                if (value === 0) return 'OK'; */
                return '';
              }
            }
          }
        },
        interaction: {
          mode: 'index',
          intersect: false
        },
        onClick: (evt: any) => {
          this.handleErrorChartClick(evt);
        },
        plugins: {
          tooltip: {
            callbacks: {
              title: (tooltipItems: any) => {
                const index = tooltipItems[0].dataIndex;
                return this.formatDateTime(this.timeLabels[index]);
              },
              afterBody: (tooltipItems: any) => {
                const index = tooltipItems[0].dataIndex;
                const errorDetail = this.errorDetails[index];
                return errorDetail ? ['', errorDetail] : [];
              }
            }
          },
          legend: {
            display: false
          }
        }
      },
      plugins: [
        {
          id: 'customCanvasBackgroundColor',
          beforeDraw: (chart: any) => {
            const ctx = chart.ctx;
            ctx.save();
            ctx.globalCompositeOperation = 'destination-over';
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, chart.width, chart.height);
            ctx.restore();
          }
        }
      ]
    });

    console.log('✅ Error Chart erstellt');
  }

  private handleMeasurementChartClick(evt: any): void {
    if (!evt.native) return;

    const points = this.measurementChart.getElementsAtEventForMode(
      evt.native,
      'nearest',
      { intersect: true },
      false
    );

    if (points.length > 0) {
      const index = points[0].index;
      if (this.chartData[index]) {
        this.reportingService
          .getFuelCellCurrents(this.samId, this.chartData[index].time)
          .subscribe((data) => {
              //console.log('(handleMeasurementChartClick) Stromfluss Diagramm data:', data);
              this.openStromflussDiagramm(data);
          });
      }
    }
  }

  private handleErrorChartClick(evt: any): void {
    if (!evt.native) return;

    const points = this.errorChart.getElementsAtEventForMode(
      evt.native,
      'nearest',
      { intersect: true },
      false
    );

    if (points.length > 0) {
      const index = points[0].index;
      if (this.errorWarningLevels[index] > 0) {
        this.showErrorDetails(index);
      }
    }
  }

  private showErrorDetails(index: number): void {
    const dataPoint = this.chartData[index];
    const errorDetail = this.errorDetails[index];

    if (!errorDetail) return;

    const message = `Zeit: ${this.timeLabels[index]}\n\n${errorDetail}`;
    const userConfirm = confirm(`${message}\n\nMöchten Sie die EFOY-Servicetool-Seite öffnen?`);

    if (userConfirm) {
      if (dataPoint.errorStatusActive && dataPoint.errorCode !== FUEL_CELL_CODE_NONE) {
        window.open(this.efoyBaseURL + dataPoint.errorCode, '_blank');
      } else if (dataPoint.fmErrorStatusActive && dataPoint.fmErrorCode !== FUEL_CELL_CODE_NONE) {
        window.open(this.efoyBaseURL + dataPoint.fmErrorCode, '_blank');
      } else if (dataPoint.warnStatusActive && dataPoint.warnCode !== FUEL_CELL_CODE_NONE) {
        window.open(this.efoyBaseURL + dataPoint.warnCode, '_blank');
      }
    }
  }

  private openStromflussDiagramm(cData: FuelCellCurrentsDataPoint): void {
    this.dialog.open(FuelcellCurrentsDiagramComponent, 
      {
        width: 'auto',
        data: cData
      }
    );
  }
}
